/**
 * fl-product-addons.js
 *
 * Lets the shopper toggle optional addon products (with their own quantity)
 * that get added to the cart TOGETHER with the main product in a single
 * /cart/add request — but as fully INDEPENDENT line items.
 *
 * No bundle-link properties are attached, so quantities can be changed (or the
 * lines removed) from the cart afterwards without affecting the main product or
 * the other addons.
 *
 * Strategy: intercept the theme's /cart/add fetch. Flaska (Minimog) posts the
 * main product as multipart FormData (a single item). When addons are toggled
 * on, we rewrite that request into Shopify's JSON `items: [...]` form so several
 * independent lines are created at once.
 */
(function () {
  'use strict';

  var SEL = {
    container: '[data-fl-addons]',
    item: '[data-fl-addon-item]',
    toggle: '[data-fl-addon-toggle]',
    qty: '[data-fl-addon-qty]',
    qtyInput: '[data-fl-qty-input]',
    qtyMinus: '[data-fl-qty-minus]',
    qtyPlus: '[data-fl-qty-plus]',
  };

  /* ------------------------------------------------------------------ *
   * Fetch interceptor (installed once, shared)
   * ------------------------------------------------------------------ */
  function installInterceptor() {
    if (window.__flAddonsFetchPatched) return;
    window.__flAddonsFetchPatched = true;

    var originalFetch = window.fetch.bind(window);

    window.fetch = function (url, config) {
      var requestUrl = typeof url === 'string' ? url : (url && url.url) || '';

      // Only touch /cart/add requests that carry a FormData body (single item add).
      if (
        !config ||
        requestUrl.indexOf('/cart/add') === -1 ||
        !(config.body instanceof FormData)
      ) {
        return originalFetch.call(this, url, config);
      }

      var selected = collectSelectedAddons();
      if (!selected.length) {
        return originalFetch.call(this, url, config);
      }

      var rewritten = buildJsonConfig(config, selected);
      if (!rewritten) {
        return originalFetch.call(this, url, config);
      }

      return originalFetch.call(this, url, rewritten);
    };
  }

  /* ------------------------------------------------------------------ *
   * Read currently-checked addons across all connected containers
   * ------------------------------------------------------------------ */
  function collectSelectedAddons() {
    var out = [];
    document.querySelectorAll(SEL.container).forEach(function (container) {
      if (!container.isConnected) return;
      container.querySelectorAll(SEL.item).forEach(function (item) {
        var toggle = item.querySelector(SEL.toggle);
        if (!toggle || !toggle.checked) return;

        var variantId = parseInt(item.getAttribute('data-addon-variant-id'), 10);
        if (!variantId) return;

        var qty = 1;
        var qtyInput = item.querySelector(SEL.qtyInput);
        if (qtyInput) {
          var parsed = parseInt(qtyInput.value, 10);
          if (parsed > 0) qty = parsed;
        }

        out.push({ id: variantId, quantity: qty });
      });
    });
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Turn the FormData single-item request into a multi-item JSON request
   * ------------------------------------------------------------------ */
  function buildJsonConfig(config, addons) {
    var formData = config.body;

    var mainId = parseInt(formData.get('id'), 10);
    if (!mainId) return null; // no variant selected — let the theme handle the error

    var mainQty = parseInt(formData.get('quantity'), 10) || 1;

    var mainProps = {};
    var hasProps = false;
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.*)\]$/);
      if (match && match[1]) {
        mainProps[match[1]] = value;
        hasProps = true;
      }
    });

    var mainItem = { id: mainId, quantity: mainQty };
    if (hasProps) mainItem.properties = mainProps;

    // Independent line items — deliberately NO bundle-link properties.
    var items = [mainItem].concat(addons);

    var sections = formData.get('sections') || '';
    var sectionsUrl = formData.get('sections_url') || window.location.pathname;

    var newConfig = Object.assign({}, config);
    newConfig.headers = Object.assign({}, config.headers || {}, {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
    });
    newConfig.body = JSON.stringify({
      items: items,
      sections: sections,
      sections_url: sectionsUrl,
    });

    return newConfig;
  }

  /* ------------------------------------------------------------------ *
   * UI: toggle reveals the quantity stepper; +/- buttons
   * ------------------------------------------------------------------ */
  function clampQty(input, delta) {
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = min;
    var next = current + delta;
    if (next < min) next = min;
    input.value = next;
  }

  function bindContainer(container) {
    if (container.__flAddonsBound) return;
    container.__flAddonsBound = true;

    container.addEventListener('change', function (e) {
      var toggle = e.target.closest(SEL.toggle);
      if (!toggle) return;
      var item = toggle.closest(SEL.item);
      if (!item) return;

      item.classList.toggle('is-active', toggle.checked);
      var qty = item.querySelector(SEL.qty);
      if (qty) qty.hidden = !toggle.checked;
    });

    container.addEventListener('click', function (e) {
      var minus = e.target.closest(SEL.qtyMinus);
      var plus = e.target.closest(SEL.qtyPlus);
      if (!minus && !plus) return;

      var item = e.target.closest(SEL.item);
      var input = item && item.querySelector(SEL.qtyInput);
      if (!input) return;

      clampQty(input, minus ? -1 : 1);
    });
  }

  function init() {
    installInterceptor();
    document.querySelectorAll(SEL.container).forEach(bindContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after theme editor section reloads / dynamic swaps.
  document.addEventListener('shopify:section:load', init);
})();
